import { CONVERSATIONS_API_URL, getConversationsAuthToken } from "@/config/env"
import type { MessageAttachment } from "@/types/conversations"

interface PreviewConversation {
  id: string
}

export interface PreviewMessage {
  id: string
  sender: "user" | "bot" | "system" | "assistant" | string
  text: string
  timestamp?: string
  created_at?: string
  attachments?: MessageAttachment[]
}

interface AddMessageResponse {
  message?: PreviewMessage
  bot_response?: PreviewMessage
  bot_message?: PreviewMessage
  turn_status?: "processing" | "completed" | "failed"
  turn_error?: string
}

const buildUrl = (path: string): string => {
  const base = (CONVERSATIONS_API_URL || "").replace(/\/$/, "")
  if (!base) return path
  return `${base}${path}`
}

const withAuthHeaders = (includeJsonContentType = true): Record<string, string> => {
  const token = getConversationsAuthToken()
  const headers: Record<string, string> = {}
  if (includeJsonContentType) headers["Content-Type"] = "application/json"
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

const request = async <T>(path: string, init?: RequestInit, includeJsonContentType = true): Promise<T> => {
  const response = await fetch(buildUrl(path), {
    ...init,
    headers: {
      ...withAuthHeaders(includeJsonContentType),
      ...(init?.headers || {}),
    },
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
  }

  if (response.status === 204) return {} as T
  return (await response.json()) as T
}

export const previewIaApi = {
  async createDemoUser() {
    const idSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const payload = {
      username: `preview_ia_${idSuffix}`,
      name: "Preview IA User",
    }
    return request<{ id: string }>("/api/v1/users/", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },

  async createConversation(userId: string) {
    const payload = {
      user: userId,
      source: "Web",
      channel_conversation_id: `preview-ia-${Date.now()}`,
      channel_user_id: `preview-ia-user-${Date.now()}`,
    }
    const result = await request<PreviewConversation | { conversation: PreviewConversation }>("/api/v1/conversations/create/", {
      method: "POST",
      body: JSON.stringify(payload),
    })
    return "conversation" in result ? result.conversation : result
  },

  async listMessages(conversationId: string) {
    const result = await request<PreviewMessage[] | { results?: PreviewMessage[] }>(
      `/api/v1/conversations/${conversationId}/messages/list/`
    )
    if (Array.isArray(result)) return result
    return Array.isArray(result.results) ? result.results : []
  },

  async sendMessage(conversationId: string, text: string, files: File[] = []) {
    const hasFiles = files.length > 0
    const body = (() => {
      if (!hasFiles) {
        return JSON.stringify({ sender: "user", text })
      }
      const formData = new FormData()
      formData.append("sender", "user")
      formData.append("text", text)
      files.forEach((file) => formData.append("files", file, file.name))
      return formData
    })()
    const result = await request<AddMessageResponse>(
      `/api/v1/conversations/${conversationId}/messages/`,
      {
        method: "POST",
        body,
      },
      !hasFiles
    )
    return {
      userMessage: result.message,
      botMessage: result.bot_response || result.bot_message,
      turnStatus: result.turn_status,
      turnError: result.turn_error,
    }
  },
}
